import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Avatar, Drawer, Typography } from 'antd';
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import type { ProviderProfile } from '../../../types/provider';
import type { LanguageCode } from '../../../types/i18n';
import { TranslatedInput } from '../../common/TranslatedInput';

const { Text } = Typography;

interface EditProfileModalProps {
  open: boolean;
  profile: ProviderProfile | null;
  saving: boolean;
  editNames: Partial<Record<LanguageCode, string>>;
  editBios: Partial<Record<LanguageCode, string>>;
  setEditNames: (next: Partial<Record<LanguageCode, string>>) => void;
  setEditBios: (next: Partial<Record<LanguageCode, string>>) => void;
  supportedLanguages: LanguageCode[];
  defaultLanguage: LanguageCode;
  onCancel: () => void;
  onSave: () => void;
}

/**
 * Profilni tahrirlash bottom sheet — gradient hero (ko'k), katta avatar,
 * polished form qatorlari va sticky footer tugmalari.
 */
export function EditProfileModal({
  open,
  profile,
  saving,
  editNames,
  editBios,
  setEditNames,
  setEditBios,
  supportedLanguages,
  defaultLanguage,
  onCancel,
  onSave,
}: EditProfileModalProps) {
  const { t } = useTranslation(['common', 'provider']);

  // Yopilish animatsiyasi davomida content saqlash uchun internal profile
  const [internalProfile, setInternalProfile] =
    useState<ProviderProfile | null>(profile);

  useEffect(() => {
    if (profile) setInternalProfile(profile);
  }, [profile]);

  const handleAfterOpenChange = (visible: boolean) => {
    if (!visible) setInternalProfile(null);
  };

  useEffect(() => {
    if (!open) return;
    const handler = () => !saving && onCancel();
    WebApp.BackButton.show();
    WebApp.BackButton.onClick(handler);
    return () => {
      WebApp.BackButton.offClick(handler);
      WebApp.BackButton.hide();
    };
  }, [open, onCancel, saving]);

  return (
    <Drawer
      open={open}
      placement="bottom"
      onClose={onCancel}
      closable={false}
      maskClosable={!saving}
      height="auto"
      afterOpenChange={handleAfterOpenChange}
      styles={{
        body: {
          padding: 0,
          background: 'var(--bl-surface-soft, #f7f8fa)',
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
        },
        wrapper: {
          borderRadius: '20px 20px 0 0',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 32px)',
        },
        mask: { background: 'rgba(15,23,42,0.45)' },
      }}
    >
      {/* Drag handle */}
      <div
        style={{
          flexShrink: 0,
          padding: '10px 0 8px',
          background: 'var(--bl-surface, #fff)',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: 'var(--bl-divider, #e5e7eb)',
            margin: '0 auto',
          }}
        />
      </div>

      {/* Hero — Profil avatari + sarlavha + close */}
      <div
        style={{
          flexShrink: 0,
          position: 'relative',
          padding: '14px 18px 18px',
          background:
            'linear-gradient(135deg, #1d4ed8 0%, #2563eb 35%, #3b82f6 70%, #60a5fa 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Decorative blur orb */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -60,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.10)',
            filter: 'blur(8px)',
            pointerEvents: 'none',
          }}
        />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(at 20% 30%, rgba(255,255,255,0.12) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          {/* Avatar */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: -5,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.18)',
                filter: 'blur(4px)',
              }}
            />
            <Avatar
              size={56}
              src={internalProfile?.photoUrl ?? undefined}
              icon={<UserOutlined />}
              style={{
                position: 'relative',
                backgroundColor: 'rgba(255,255,255,0.22)',
                fontSize: 24,
                border: '3px solid rgba(255,255,255,0.42)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
              }}
            />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'rgba(255,255,255,0.78)',
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                marginBottom: 2,
              }}
            >
              {t('provider:profile.editModal.subtitle')}
            </Text>
            <Text
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: '#fff',
                letterSpacing: -0.3,
                lineHeight: 1.2,
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('provider:profile.editModal.title')}
            </Text>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            aria-label={t('common:actions.cancel')}
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.22)',
              background: 'rgba(255,255,255,0.18)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              WebkitTapHighlightColor: 'transparent',
              flexShrink: 0,
              outline: 'none',
            }}
          >
            <CloseOutlined />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '14px 16px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <FieldCard
          icon={<UserOutlined />}
          iconColor="#3390ec"
          label={t('provider:profile.editModal.nameField')}
          required
          progressSlot={
            supportedLanguages.length > 1 ? (
              <LanguageProgress
                values={editNames}
                supportedLanguages={supportedLanguages}
                defaultLanguage={defaultLanguage}
              />
            ) : null
          }
        >
          <TranslatedInput
            value={editNames}
            onChange={setEditNames}
            supportedLanguages={supportedLanguages}
            defaultLanguage={defaultLanguage}
            required
            placeholder={t('provider:profile.editModal.namePlaceholder')}
            layout="stacked"
          />
          {supportedLanguages.length > 1 && (
            <MissingTranslationsBanner
              values={editNames}
              supportedLanguages={supportedLanguages}
              defaultLanguage={defaultLanguage}
            />
          )}
        </FieldCard>

        <FieldCard
          icon={<EditOutlined />}
          iconColor="#9254de"
          label={t('provider:profile.editModal.bioField')}
          hintAfterLabel={t('provider:profile.editModal.bioOptional')}
          progressSlot={
            supportedLanguages.length > 1 ? (
              <LanguageProgress
                values={editBios}
                supportedLanguages={supportedLanguages}
                defaultLanguage={defaultLanguage}
              />
            ) : null
          }
        >
          <TranslatedInput
            value={editBios}
            onChange={setEditBios}
            supportedLanguages={supportedLanguages}
            defaultLanguage={defaultLanguage}
            multiline
            placeholder={t('provider:profile.editModal.bioPlaceholder')}
            layout="stacked"
          />
        </FieldCard>
      </div>

      {/* Sticky footer */}
      <div
        style={{
          flexShrink: 0,
          padding: '12px 16px calc(14px + env(safe-area-inset-bottom, 0px))',
          background: 'var(--bl-surface, #fff)',
          borderTop: '1px solid var(--bl-divider, #ebebeb)',
          display: 'flex',
          gap: 8,
        }}
      >
        <SecondaryButton
          label={t('common:actions.cancel')}
          disabled={saving}
          onClick={onCancel}
        />
        <PrimaryButton
          icon={<SaveOutlined />}
          label={t('common:actions.save')}
          loading={saving}
          onClick={onSave}
        />
      </div>
    </Drawer>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

interface FieldCardProps {
  icon: ReactNode;
  iconColor: string;
  label: string;
  required?: boolean;
  hintAfterLabel?: string;
  progressSlot?: ReactNode;
  children: ReactNode;
}

function FieldCard({
  icon,
  iconColor,
  label,
  required,
  hintAfterLabel,
  progressSlot,
  children,
}: FieldCardProps) {
  return (
    <div
      style={{
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        borderRadius: 14,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 8,
            background: `linear-gradient(135deg, ${iconColor}14 0%, ${iconColor}26 100%)`,
            border: `1px solid ${iconColor}1a`,
            color: iconColor,
            fontSize: 12,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <Text
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--bl-text-muted, #6b7280)',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          {label}
          {required && <span style={{ color: '#d93b3e', marginLeft: 3 }}>*</span>}
          {hintAfterLabel && (
            <span
              style={{
                marginLeft: 6,
                fontWeight: 500,
                color: 'var(--bl-text-muted, #9ca3af)',
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              {hintAfterLabel}
            </span>
          )}
        </Text>
        {progressSlot}
      </div>
      {children}
    </div>
  );
}

// ─── Language Progress (X/Y) ────────────────────────────────────────────────

function LanguageProgress({
  values,
  supportedLanguages,
  defaultLanguage,
}: {
  values: Partial<Record<LanguageCode, string>>;
  supportedLanguages: LanguageCode[];
  defaultLanguage: LanguageCode;
}) {
  const { t } = useTranslation('provider');
  const filledCount = supportedLanguages.filter(
    (lang) => (values[lang] ?? '').trim().length > 0,
  ).length;
  const total = supportedLanguages.length;
  const complete = filledCount === total;
  const accent = complete ? '#1f8b4c' : '#fa8c16';

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
        padding: '3px 9px',
        borderRadius: 999,
        background: `${accent}14`,
        border: `1px solid ${accent}26`,
      }}
    >
      {/* Progress dots */}
      <div style={{ display: 'inline-flex', gap: 3 }}>
        {supportedLanguages.map((lang) => {
          const isFilled = (values[lang] ?? '').trim().length > 0;
          const isDefault = lang === defaultLanguage;
          return (
            <span
              key={lang}
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: isFilled ? accent : `${accent}3a`,
                border: isDefault && isFilled ? `1px solid ${accent}` : 'none',
              }}
            />
          );
        })}
      </div>
      <Text
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: accent,
          letterSpacing: 0.2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {t('profile.editModal.progress', { filled: filledCount, total })}
      </Text>
    </div>
  );
}

// ─── Missing translations banner ────────────────────────────────────────────

function MissingTranslationsBanner({
  values,
  supportedLanguages,
  defaultLanguage,
}: {
  values: Partial<Record<LanguageCode, string>>;
  supportedLanguages: LanguageCode[];
  defaultLanguage: LanguageCode;
}) {
  const { t } = useTranslation('provider');
  const defaultFilled = (values[defaultLanguage] ?? '').trim().length > 0;
  const missingLangs = supportedLanguages.filter(
    (lang) => (values[lang] ?? '').trim().length === 0,
  );

  // Show only when default is filled but other languages are empty
  if (!defaultFilled || missingLangs.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 12px',
        background:
          'linear-gradient(135deg, rgba(250,140,22,0.06) 0%, rgba(250,140,22,0.16) 100%)',
        border: '1px solid rgba(250,140,22,0.28)',
        borderRadius: 10,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 9,
          background:
            'linear-gradient(135deg, rgba(250,140,22,0.16) 0%, rgba(250,140,22,0.32) 100%)',
          border: '1px solid rgba(250,140,22,0.22)',
          color: '#d46b08',
          fontSize: 13,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <WarningOutlined />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#d46b08',
            display: 'block',
            marginBottom: 2,
          }}
        >
          {t('profile.editModal.missingTranslations')}
        </Text>
        <Text
          style={{
            fontSize: 11,
            color: 'var(--bl-text-muted, #6b7280)',
            display: 'block',
            lineHeight: 1.4,
          }}
        >
          {t('profile.editModal.missingTranslationsHint')}
        </Text>
      </div>
    </div>
  );
}

function PrimaryButton({
  icon,
  label,
  loading,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  loading: boolean;
  onClick: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={() => !loading && onClick()}
      disabled={loading}
      onTouchStart={() => !loading && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => !loading && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        flex: 2,
        minHeight: 48,
        padding: '0 16px',
        border: '1px solid rgba(22,119,255,0.18)',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #3390ec 0%, #1677ff 100%)',
        color: '#fff',
        fontSize: 15,
        fontWeight: 700,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.85 : 1,
        boxShadow: '0 4px 14px rgba(22,119,255,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
        transform: pressed && !loading ? 'scale(0.985)' : 'scale(1)',
        transition: 'transform 0.1s ease, opacity 0.15s ease',
        WebkitTapHighlightColor: 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        outline: 'none',
        letterSpacing: 0.15,
        fontFamily: 'inherit',
      }}
    >
      {loading ? <LoadingOutlined spin /> : icon}
      <span>{label}</span>
    </button>
  );
}

function SecondaryButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick()}
      disabled={disabled}
      onTouchStart={() => !disabled && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => !disabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        flex: 1,
        minHeight: 48,
        padding: '0 16px',
        border: 'none',
        borderRadius: 12,
        background: pressed
          ? 'var(--bl-surface-soft, #ebedf0)'
          : 'var(--bl-surface-soft, #f7f8fa)',
        color: 'var(--bl-text-muted, #6b7280)',
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transform: pressed && !disabled ? 'scale(0.985)' : 'scale(1)',
        transition: 'background 0.15s ease, transform 0.1s ease',
        WebkitTapHighlightColor: 'transparent',
        opacity: disabled ? 0.55 : 1,
        outline: 'none',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}
